import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { cx } from '../../utils';
import styles from './CdTypography.module.scss';

export type CdHeadingSize = 'display' | 'xl' | 'lg' | 'md' | 'sm';

export interface CdHeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Semantic level – independent from the visual size. */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  size?: CdHeadingSize;
  children: ReactNode;
}

/**
 * Heading scale (PermiOne on Carbon type tokens):
 * display = hero title, xl = page/section title, lg = card title, md = tertiary heading, sm = label heading.
 */
export const CdHeading = ({
  level = 2,
  size = 'lg',
  className,
  children,
  ...rest
}: CdHeadingProps) => {
  const Tag = `h${level}` as ElementType;
  return (
    <Tag className={cx(styles.heading, styles[size], className)} {...rest}>
      {children}
    </Tag>
  );
};

export type CdTextVariant = 'body' | 'bodyLarge' | 'label' | 'small';

export interface CdTextProps extends HTMLAttributes<HTMLElement> {
  variant?: CdTextVariant;
  tone?: 'primary' | 'secondary' | 'onDark' | 'onDarkMuted';
  as?: 'p' | 'span' | 'div' | 'strong';
  children: ReactNode;
}

export const CdText = ({
  variant = 'body',
  tone = 'primary',
  as: Tag = 'p',
  className,
  children,
  ...rest
}: CdTextProps) => {
  return (
    <Tag className={cx(styles.text, styles[variant], styles[tone], className)} {...rest}>
      {children}
    </Tag>
  );
};
